import { usePredefinedMessage } from '@/container/daycare/daycareProfile/customisation/predefinedMessage/usePredefinedMessage'
import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { DaycarePredefinedMessageType } from '@/types/Daycare'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './confirmRegularRequest.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close, Transform } from '@mui/icons-material'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { OccasionalSlotsPlaces, RegularCancellationRequest } from '@/types/Slots'
import { ACCEPT_CANCELLATION_REQUEST } from '@/modules/API/slot/regularUpdate'
import TransformRegularRequest from '../transformRegularRequest/transformRegularRequest'
import moment, { Moment } from 'moment-timezone'
import { ChildWithParents } from '@/types/Child'
import TextArea from '@/components/_custom/TextArea/textArea'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { renderEventForRegularConflict } from '@/container/planning/utils/calendarEvents.tsx'
import { CalendarEventType } from '@/types/Calendar.ts'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'

interface ConfirmRegularRequestProps {
    open: boolean
    handleClose: () => void
    refetchData: () => void
    closeParentDialog: () => void
    regularRequests: RegularCancellationRequest[]
    accepted: boolean
    childs: ChildWithParents[]
    places: OccasionalSlotsPlaces[]
    isRpeOrLaep: boolean
}

const ConfirmRegularRequest: FunctionComponent<ConfirmRegularRequestProps> = ({
    open,
    refetchData,
    closeParentDialog,
    regularRequests,
    handleClose,
    accepted,
    childs,
    places,
    isRpeOrLaep
}: ConfirmRegularRequestProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const {
        message: comment,
        textAreaProps,
        dialog
    } = usePredefinedMessage(
        accepted ? DaycarePredefinedMessageType.acceptRegularRequest : DaycarePredefinedMessageType.rejectRegularRequest
    )

    const [isTransformRegularRequestDialogOpen, setTransformRegularRequestDialogOpen] = useState(false)

    const [selectChilds, setSelectChilds] = useState<string[]>(
        regularRequests.map((regularRequest) => {
            return regularRequest.id.toString()
        })
    )

    const [haveDouble, setHaveDouble] = useState<boolean>(false)

    useEffect(() => {
        setSelectChilds(regularRequests.map((regularRequest) => regularRequest.id.toString()))

        const allChildIds: number[] = regularRequests
            .filter((regular: RegularCancellationRequest): boolean => !!regular.childId && regular.childId !== 0)
            .map((regular: RegularCancellationRequest): number => regular.childId as number)

        const uniqueIds: Set<number> = new Set(allChildIds)

        setHaveDouble(uniqueIds.size !== allChildIds.length)
    }, [regularRequests])

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: regularRequests.map((regularRequest: RegularCancellationRequest): SelectOptionDeprecated => {
                return {
                    name:
                        regularRequest.childName +
                        (regularRequest.childComplement ? ' - ' + regularRequest.childComplement : '') +
                        (haveDouble ? ' - ' + regularRequest.date : ''),
                    value: regularRequest.id.toString()
                }
            })
        }
    ]

    const [confirmRegularRequest, { loading: loadingAccept }] = useMutation(ACCEPT_CANCELLATION_REQUEST)

    async function confirmAllRegularRequest(): Promise<void> {
        const requestsToConfirm: RegularCancellationRequest[] = regularRequests.filter(
            (regularRequest: RegularCancellationRequest): boolean => selectChilds.includes(regularRequest.id.toString())
        )

        try {
            for (const regularRequest of requestsToConfirm) {
                await confirmRegularRequest({
                    variables: {
                        daycare_id: daycare.id,
                        request: regularRequest.id,
                        accepted: accepted,
                        daycare_comment: comment ? comment : null
                    }
                })
            }

            refetchData()
            handleClose()
            closeParentDialog()
            showNotificationSuccess(accepted ? t('acceptRequestSlotSuccess') : t('slotRequestCancelled'))
        } catch {
            showNotificationError(accepted ? t('errorAcceptedRequestSlot') : t('errorCancelledSlotRequest'))
        }
    }

    const canTransform: boolean = regularRequests.some((regularRequest: RegularCancellationRequest) => {
        const { start, end, regularSlotStart, regularSlotEnd } = regularRequest
        const startMoment: Moment = moment(start, 'HH:mm:ss')
        const endMoment: Moment = moment(end, 'HH:mm:ss')
        const regularSlotStartMoment: Moment = moment(regularSlotStart, 'HH:mm:ss')
        const regularSlotEndMoment: Moment = moment(regularSlotEnd, 'HH:mm:ss')

        if (startMoment.isSame(regularSlotStartMoment, 'minute') && endMoment.isSame(regularSlotEndMoment, 'minute')) {
            return true
        }

        return startMoment.isAfter(regularSlotStartMoment) || endMoment.isBefore(regularSlotEndMoment)
    })

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader title={accepted ? t('acceptRegularRequest') : t('refuseRegularRequest')} />
                <DialogBody className={styles.DialogBody}>
                    <p className={styles.Text}>
                        {accepted ? t('acceptRegularRequestMessage') : t('refuseRegularRequestMessage')}
                    </p>
                    {regularRequests.length === 1 ? (
                        <div className={styles.UserInput}>
                            {renderEventForRegularConflict({
                                title: defineTitle(regularRequests[0]),
                                date: moment(regularRequests[0].date).toDate(),
                                start: moment(regularRequests[0].start, 'HH:mm:ss').toDate(),
                                end: moment(regularRequests[0].start, 'HH:mm:ss').toDate(),
                                slotId: regularRequests[0].id,
                                childId: regularRequests[0].childId,
                                type: CalendarEventType.REGULAR_REQUEST
                            })}
                        </div>
                    ) : (
                        <SelectMultiple
                            selectOptions={selectOptionsChild}
                            selectValues={selectChilds}
                            setSelectValues={setSelectChilds}
                            className={styles.UserInput}
                        />
                    )}
                    <div className={styles.TextAreaInput}>
                        <p className={styles.TextAreaTitle}>{t('comment')}</p>
                        <TextArea placeholder={t('comment')} className={styles.UserInput} {...textAreaProps} />
                    </div>
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('yes')}
                            customIcon={<Check />}
                            onClick={confirmAllRegularRequest}
                            className={styles.Button}
                            disabled={loadingAccept}
                        />
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('no')}
                            customIcon={<Close />}
                            onClick={handleClose}
                            className={styles.Button}
                        />
                    </div>
                    {accepted && canTransform && (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('yesAndTransform')}
                                customIcon={<Transform />}
                                onClick={() => setTransformRegularRequestDialogOpen(true)}
                                className={styles.ButtonFullWidth}
                            />
                        </div>
                    )}
                    {dialog}
                </DialogBody>
            </Dialog>
            {isTransformRegularRequestDialogOpen && (
                <TransformRegularRequest
                    open={isTransformRegularRequestDialogOpen}
                    handleClose={() => setTransformRegularRequestDialogOpen(false)}
                    daycare={daycare}
                    childs={childs}
                    regularRequests={regularRequests}
                    confirmAllRegularRequest={confirmAllRegularRequest}
                    places={places}
                    isRpeOrLaep={isRpeOrLaep}
                />
            )}
        </>
    )
}

export default ConfirmRegularRequest

function defineTitle(slot: RegularCancellationRequest): string {
    return `${moment(slot.start, 'HH:mm:ss').format('HH:mm')} - ${moment(slot.end, 'HH:mm:ss').format('HH:mm')} - ${slot.date} - ${slot.childName}`
}
